import React, { type CSSProperties, useState } from "react";

interface EditableFieldProps {
  value: string;
  placeholder: string;
  onSave: (value: string) => void;
  style?: CSSProperties;
  maxLength?: number;
  // Preview/PDF: render the value as static text (no editing affordances) so canvas and preview match.
  readOnly?: boolean;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  value,
  placeholder,
  onSave,
  style,
  maxLength,
  readOnly
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const commit = () => {
    setEditing(false);
    onSave(draft);
  };

  // Static render for preview: keep the same box metrics (padding/margin) as the editable state so
  // the header lays out identically, but drop the cursor, hover title and empty-state placeholder.
  if (readOnly) {
    return (
      <div
        style={{
          borderRadius: 6,
          padding: "2px 6px",
          margin: "-2px -6px",
          overflowWrap: "break-word",
          wordBreak: "break-word",
          ...style
        }}
      >
        {value}
      </div>
    );
  }

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        maxLength={maxLength}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setDraft(value);
            setEditing(false);
          }
        }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "rgba(255,255,255,0.15)",
          border: "1px solid rgba(255,255,255,0.4)",
          borderRadius: 6,
          outline: "none",
          color: "inherit",
          fontFamily: "inherit",
          padding: "4px 8px",
          width: "100%",
          ...style
        }}
      />
    );
  }

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        setDraft(value);
        setEditing(true);
      }}
      title="Click to edit"
      style={{
        cursor: "text",
        borderRadius: 6,
        padding: "2px 6px",
        margin: "-2px -6px",
        overflowWrap: "break-word",
        wordBreak: "break-word",
        ...style
      }}
    >
      {value || <span style={{ opacity: 0.45 }}>{placeholder}</span>}
    </div>
  );
};
